import { Component, OnInit } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { DocumentService } from './document.service';
import { DocumentTypeService } from '../document-type/document-type.service';
import { DocumentRequest } from './document.model';

interface Language {
    languageId: number;
    code: string;
    name: string;
    icon: string;
}

interface DocumentType {
    documentTypeId: number;
    name: string;
}

@Component({
    selector: 'app-document',
    standalone: false,
    templateUrl: './document.component.html',
    styleUrls: ['./document.component.css']
})
export class DocumentComponent implements OnInit {

    successMessage = '';
    errorMessage = '';

    languages: Language[] = [];
    documents: any[] = [];
    filteredDocuments: any[] = [];
    documentTypes: DocumentType[] = [];

    currentPage = 1;
    pageSize = 6;

    activeTab = 'all';
    activeTabPopup = 1;
    searchText = '';

    isModalOpen = false;

    isEnableSave = false;
    isEnableUpdate = false;
    isEnableDelete = false;

    documentId = 0;
    documentTypeId: number | null = null;

    publishDate = this.getTodayIsoDate();
    isPublished = false;
    pdfFile: File | null = null;

    titleKh = '';
    subTitleKh = '';
    titleEn = '';
    subTitleEn = '';

    constructor(
        private documentService: DocumentService,
        private documentTypeService: DocumentTypeService,
        private languageService: LanguageService
    ) { }

    ngOnInit(): void {
        this.loadLanguages();
        this.loadDocumentTypes();
        this.loadDocuments('');
    }

    private getTodayIsoDate(): string {
        return new Date().toISOString().split('T')[0];
    }

    loadLanguages(): void {
        this.languageService.getAll().subscribe({
            next: (res: any) => {
                this.languages = (res.data || [])
                    .sort((a: any, b: any) => a.languageId - b.languageId);

                if (this.languages.length) {
                    this.setTab(this.languages[0].code);
                    this.setTabPopup(this.languages[0].languageId);
                }
            },
            error: () => this.showError('Failed to load languages.')
        });
    }

    loadDocumentTypes(): void {
        this.documentTypeService.getAllDocumentTypes('').subscribe({
            next: (res: any) => {
                this.documentTypes = (res.data || []).map((item: any) => ({
                    documentTypeId: item.documentTypeId,
                    name:
                        item.translations?.find(
                            (t: any) => t.languageCode === 'en'
                        )?.name ||
                        item.translations?.[0]?.name ||
                        'Unknown'
                }));
            },
            error: () => this.showError('Failed to load document types.')
        });
    }

    loadDocuments(langCode: string): void {
        this.documentService.getAllDocuments(langCode).subscribe({
            next: (res: any) => {
                const apiData = res.data || [];

                this.documents = apiData
                    .filter((item: any) => item.translations?.length)
                    .flatMap((item: any) =>
                        item.translations.map((t: any) => ({
                            documentId: item.documentId,
                            documentTypeId: item.documentTypeId,
                            documentTypeName: t.documentTypeName,
                            publishDate: item.publishDate,
                            isPublished: item.isPublished,
                            pdfUrl: item.pdfUrl,
                            languageCode: t.languageCode,
                            languageId: t.languageId,
                            title: t.title,
                            subTitle: t.subTitle
                        }))
                    );

                this.filterDocuments(this.activeTab);
            },
            error: () =>
                this.showError('Failed to load documents.')
        });
    }

    setTab(tab: string): void {
        this.activeTab = tab;
        this.currentPage = 1;
        this.filterDocuments(tab);
    }

    setTabPopup(languageId: number): void {
        this.activeTabPopup = languageId;
    }

    filterDocuments(tab: string): void {
        const keyword = this.searchText.trim().toLowerCase();
        this.currentPage = 1;

        this.filteredDocuments = this.documents.filter(document => {
            const status = document.isPublished ? 'Published' : 'Draft';
            const matchesTab = tab === 'all' || document.languageCode === tab;
            const matchesSearch = !keyword ||
                `${document.documentTypeName ?? ''} ${document.title ?? ''} ${document.subTitle ?? ''} ${document.publishDate ?? ''} ${status} ${document.languageCode ?? ''}`
                    .toLowerCase()
                    .includes(keyword);

            return matchesTab && matchesSearch;
        });
    }

    get pagedDocuments(): any[] {
        const start =
            (this.currentPage - 1) * this.pageSize;

        return this.filteredDocuments.slice(
            start,
            start + this.pageSize
        );
    }

    totalPages(): number {
        return Math.max(
            1,
            Math.ceil(
                this.filteredDocuments.length /
                this.pageSize
            )
        );
    }

    changePage(page: number): void {
        this.currentPage = page;
    }

    getLanguageCount(code: string): number {
        return this.documents.filter(
            d => d.languageCode === code
        ).length;
    }

    onFileSelected(event: any): void {
        this.pdfFile =
            event.target.files?.[0] || null;
    }

    addNew(): void {
        this.resetForm();
        this.openModal('Add');
    }

    viewData(
        id: number,
        mode: 'Edit' | 'Delete'
    ): void {
        this.documentService
            .getDocumentById(id, this.activeTab)
            .subscribe({
                next: (res: any) => {
                    const data = res.data;

                    if (!data) {
                        this.showError(
                            'Document not found.'
                        );
                        return;
                    }

                    this.documentId = data.documentId;
                    this.documentTypeId =
                        data.documentTypeId;

                    this.publishDate =
                        data.publishDate?.split('T')[0] ||
                        this.getTodayIsoDate();

                    this.isPublished =
                        data.isPublished;

                    this.titleKh = '';
                    this.subTitleKh = '';
                    this.titleEn = '';
                    this.subTitleEn = '';

                    data.translations.forEach(
                        (t: any) => {
                            if (
                                t.languageCode === 'kh'
                            ) {
                                this.titleKh = t.title;
                                this.subTitleKh =
                                    t.subTitle || '';
                            }

                            if (
                                t.languageCode === 'en'
                            ) {
                                this.titleEn = t.title;
                                this.subTitleEn =
                                    t.subTitle || '';
                            }
                        }
                    );

                    this.activeTabPopup =
                        this.activeTab === 'kh'
                            ? 1
                            : 2;

                    this.pdfFile = null;

                    this.openModal(mode);
                },
                error: () =>
                    this.showError(
                        'Unable to load document.'
                    )
            });
    }

    save(): void {
        if (!this.validateForm()) {
            return;
        }

        this.documentService
            .create({
                data: this.buildPayload(),
                pdf: this.pdfFile
            })
            .subscribe({
                next: () => {
                    this.showSuccess(
                        'Document created successfully.'
                    );
                    this.afterSuccess();
                },
                error: () =>
                    this.showError(
                        'Failed to create document.'
                    )
            });
    }

    update(): void {
        if (!this.validateForm()) {
            return;
        }

        this.documentService
            .update(this.documentId, {
                data: this.buildPayload(),
                pdf: this.pdfFile
            })
            .subscribe({
                next: () => {
                    this.showSuccess(
                        'Document updated successfully.'
                    );
                    this.afterSuccess();
                },
                error: () =>
                    this.showError(
                        'Failed to update document.'
                    )
            });
    }

    delete(): void {
        if (!this.documentId) {
            this.showError(
                'Please select a document.'
            );
            return;
        }

        if (
            !confirm(
                'Are you sure you want to delete this document?'
            )
        ) {
            return;
        }

        this.documentService
            .delete(this.documentId)
            .subscribe({
                next: () => {
                    this.showSuccess(
                        'Document deleted successfully.'
                    );
                    this.afterSuccess();
                },
                error: () =>
                    this.showError(
                        'Failed to delete document.'
                    )
            });
    }

    private buildPayload(): DocumentRequest {
        return {
            documentId:
                this.documentId || undefined,
            documentTypeId:
                this.documentTypeId!,
            publishDate: this.publishDate,
            isPublished: this.isPublished,
            translations: [
                {
                    languageId: 1,
                    title: this.titleKh,
                    subTitle: this.subTitleKh
                },
                {
                    languageId: 2,
                    title: this.titleEn,
                    subTitle: this.subTitleEn
                }
            ]
        };
    }

    private validateForm(): boolean {
        if (!this.documentTypeId) {
            this.showError(
                'Please choose a document type.'
            );
            return false;
        }

        if (
            !this.titleKh.trim() ||
            !this.titleEn.trim()
        ) {
            this.showError(
                'Please enter titles for both languages.'
            );
            return false;
        }

        return true;
    }

    private afterSuccess(): void {
        this.closeModal();
        this.resetForm();
        this.loadDocuments(this.activeTab);
    }

    private showSuccess(
        message: string
    ): void {
        this.successMessage = message;

        setTimeout(
            () => this.clearMessages(),
            3000
        );
    }

    private showError(
        message: string
    ): void {
        this.errorMessage = message;

        setTimeout(
            () => this.clearMessages(),
            3000
        );
    }

    private clearMessages(): void {
        this.successMessage = '';
        this.errorMessage = '';
    }

    openModal(
        eventName?: string
    ): void {
        this.visibleButtonControl(
            eventName || ''
        );

        this.isModalOpen = true;
    }

    closeModal(): void {
        this.isModalOpen = false;
    }

    visibleButtonControl(
        eventName: string
    ): void {
        this.isEnableSave =
            eventName === 'Add';

        this.isEnableUpdate =
            eventName === 'Edit';

        this.isEnableDelete =
            eventName === 'Delete';
    }

    private resetForm(): void {
        this.documentId = 0;
        this.documentTypeId = null;
        this.publishDate =
            this.getTodayIsoDate();

        this.isPublished = false;
        this.pdfFile = null;

        this.titleKh = '';
        this.subTitleKh = '';

        this.titleEn = '';
        this.subTitleEn = '';

        this.activeTabPopup =
            this.languages[0]?.languageId || 1;
    }


}
